import { addAlert } from 'components/shared/Alert';
import {
    getKeywordsPage, getKeywordsData, trackKeyword, getProduct,
    deepSearch, getHistory, seeReport, getCategories
} from './utils';
import Types from './types';
import {
    fetch,
    setLoading,
    setError,
    setData,
    updateData,
    setCategories,
    updateCategories,
    fetchCategoriesFail,
    clearSelected,
    addTracked,
    setProduct,
    setEnd,
    updatePagination,
    setHistory,
    setTotalKeywords,
    setSearchQuery,
    setNoResults,
} from './actions';

const suggestionMiddleware = ({ dispatch, getState }) => (next) => async (action) => {
    const state = getState().suggestions.toJS();
    const {
        isStrict,
        appliedFilter,
        categories: { pinned },
        sort,
        isLoading
    } = state;

    next(action);
    try {
        if ([
            Types.PIN_CATEGORY,
            Types.REMOVE_CATEGORY,
            Types.SET_STRICT_SEARCH,
        ].includes(action.type) || (action.type === Types.RESET_FILTER && action.payload)) {
            dispatch(fetch('Peek'));
        } else if (action.type === Types.FETCH) {
            if (!isLoading) {
                dispatch(setLoading(true));
                try {
                    const { query, page, currentPage } = action.meta;
                    if (currentPage !== 'Peek' && query.trim() === '') {
                        throw Error();
                    }
                    const fetchFunction = currentPage === 'Peek' ? getKeywordsData : getKeywordsPage;
                    const fetchProps = currentPage === 'Peek' ? {
                        categories: pinned,
                        filters: appliedFilter,
                        isStrict,
                        page,
                        sort
                    } : { asin: query, page };
                    const { data, pagination } = await fetchFunction(fetchProps);
                    dispatch(updatePagination(pagination));
                    if (data.length === 0) {
                        dispatch(setEnd());
                        if (page === 1) dispatch(setNoResults());
                    } else if (page > 1) {
                        dispatch(updateData(data));
                    } else {
                        dispatch(setData(data));
                    }
                    if (data.length < 50 || (currentPage === 'Peek' && page === pagination.lastPage)) {
                        dispatch(setEnd());
                    }
                } catch (err) {
                    dispatch(setError(['Something went wrong']));
                } finally {
                    dispatch(setLoading(false));
                }
            }
        } else if (action.type === Types.APPLY_FILTER) {
            if (action.page !== 'Deep Words') {
                dispatch(fetch(action.page));
            }
        } else if (action.type === Types.TRACK_KEYWORDS) {
            const { keywords, asin, productId } = action.meta;
            const keywordsCount = keywords.size;
            const promises = keywords.map((keyword) => trackKeyword({
                active: true,
                asin,
                keyword,
                productId,
            }));

            Promise.all(promises)
                .then(((results) => {
                    results.forEach((res) => {
                        dispatch(addTracked(res.id, res.keyword));
                    });
                    addAlert({ message: `${keywordsCount} new keyword${keywordsCount > 1 ? 's' : ''} tracked` });
                    dispatch(clearSelected());
                })).catch(() => {
                    // console.error(err);
                });
        } else if (action.type === Types.FETCH_PRODUCT) {
            const { asin } = action.meta;
            try {
                const res = await getProduct(asin);
                dispatch(setProduct(res.data));
            } catch (err) {
                // console.error(err);
            }
        } else if (action.type === Types.FETCH_HISTORY) {
            try {
                const res = await getHistory();
                dispatch(setHistory(res));
            } catch (err) {
                // console.error(err);
            }
        } else if (action.type === Types.DEEP_SEARCH) {
            if (!isLoading) {
                const { keyword } = action.meta;
                dispatch(setLoading(true));
                try {
                    const { data, words, message, status } = await deepSearch(keyword);
                    if (status === 202) {
                        dispatch(setError([message]));
                    } else {
                        if (data.length === 0) {
                            dispatch(setNoResults());
                        }
                        dispatch(setData(data));
                        dispatch(setTotalKeywords(words));
                        dispatch(setEnd());
                    }
                } catch (err) {
                    if (err.status === 504) {
                        dispatch(setError(['Wait a minute and try again']));
                    } else {
                        dispatch(setError([err.message || 'Something went wrong']));
                    }
                } finally {
                    dispatch(setLoading(false));
                }
            }
        } else if (action.type === Types.SEE_REPORT) {
            const { id } = action.meta;
            dispatch(setLoading(true));
            try {
                const { keyword, data, words } = await seeReport(id);
                if (data.length === 0) {
                    dispatch(setNoResults());
                }
                dispatch(setSearchQuery(keyword));
                dispatch(setData(data));
                dispatch(setTotalKeywords(words));
                dispatch(setEnd());
            } catch (err) {
                // console.error(err);
            } finally {
                dispatch(setLoading(false));
            }
        } else if (action.type === Types.FETCH_CATEGORIES) {
            const { str, page } = action.payload;
            try {
                const res = await getCategories(str, page);
                if (page > 0) {
                    dispatch(updateCategories(res));
                } else {
                    dispatch(setCategories(res));
                }
            } catch (err) {
                dispatch(fetchCategoriesFail());
            }
        }
    } catch (err) {
        // console.error(err);
    }
};

export default suggestionMiddleware;
